import React, { useState } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';

const ReviewDraftQuestionModal = ({ isOpen, onClose, question, onSubmit }) => {
  const [reviewStatus, setReviewStatus] = useState('APPROVED');
  const [reviewComments, setReviewComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (reviewStatus === 'NEEDS_WORK' && !reviewComments.trim()) {
      setError('Comments are required when requesting changes');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onSubmit(question.id, {
        status: reviewStatus,
        reviewComments: reviewComments.trim()
      });
      onClose();
      // Reset form
      setReviewStatus('APPROVED');
      setReviewComments('');
    } catch (error) {
      console.error('Error submitting review:', error);
      setError(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReviewStatus('APPROVED');
    setReviewComments('');
    setError('');
    onClose();
  };

  if (!isOpen || !question) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Review Draft Question</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Question Preview */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Question to Review</h3>
            
            {/* Question Text */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-gray-700 mb-2">Question:</h4>
              <p className="text-gray-800 whitespace-pre-wrap">{question.questionText}</p>
            </div>

            {/* Options */}
            {question.options && question.options.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Options:</h4>
                <div className="space-y-2">
                  {question.options.map((option, index) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-3 p-3 rounded-lg border ${
                        option.isCorrect
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        option.isCorrect
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <span className="flex-1 text-gray-800">{option.text}</span>
                      {option.isCorrect && (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Question Details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Subject:</span>
                <span className="ml-2 text-gray-800">{question.subject}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Difficulty:</span>
                <span className="ml-2 text-gray-800">{question.difficultyLevel}</span>
              </div>
              {question.topic && (
                <div>
                  <span className="font-medium text-gray-700">Topic:</span>
                  <span className="ml-2 text-gray-800">{question.topic}</span>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700">Created by:</span>
                <span className="ml-2 text-gray-800">{question.createdBy}</span>
              </div>
            </div>
          </div>

          {/* Review Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Review Decision */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Review Decision *
              </label>
              <div className="space-y-3">
                <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="reviewStatus"
                    value="APPROVED"
                    checked={reviewStatus === 'APPROVED'}
                    onChange={(e) => setReviewStatus(e.target.value)}
                    className="text-green-600 focus:ring-green-500"
                  />
                  <div className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="font-medium text-gray-900">Approve</div>
                      <div className="text-sm text-gray-600">Question is ready for publishing</div>
                    </div>
                  </div>
                </label>

                <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="reviewStatus"
                    value="NEEDS_WORK"
                    checked={reviewStatus === 'NEEDS_WORK'}
                    onChange={(e) => setReviewStatus(e.target.value)}
                    className="text-yellow-600 focus:ring-yellow-500"
                  />
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    <div>
                      <div className="font-medium text-gray-900">Needs Work</div>
                      <div className="text-sm text-gray-600">Question requires modifications</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Review Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Comments {reviewStatus === 'NEEDS_WORK' && '*'}
              </label>
              <textarea
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                placeholder={
                  reviewStatus === 'APPROVED'
                    ? 'Optional: Add any comments about the question...'
                    : 'Please specify what needs to be changed or improved...'
                }
                required={reviewStatus === 'NEEDS_WORK'}
              />
              {reviewStatus === 'NEEDS_WORK' && (
                <p className="mt-1 text-sm text-gray-600">
                  Please provide specific feedback on what needs to be improved.
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-6 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                  reviewStatus === 'APPROVED'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
                disabled={loading}
              >
                {loading ? 'Submitting...' : (
                  reviewStatus === 'APPROVED' ? 'Approve Question' : 'Request Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReviewDraftQuestionModal;
